import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Table } from "antd"
import { getWebhookLogs } from "@/api/webhooks"
import { Loader } from "@/UI"
import {
  GetWebhookLogsFilters,
  IWebhook,
  IWebhookLog,
  WebhookLogStatus,
} from "@/types/webhooks.types"
import { DateTime } from "luxon"
import OrderTableExpandIcon from "@/modules/active-orders/OrderTable/components/OrderTableExpandIcon"
import { capitalize } from "lodash"
import DateRangeFilter from "@/UI/Filters/DateRangeFilter"
import SearchFilter from "@/UI/Filters/SearchFilter"
import { DateFilterType, LUXON_PICKER_DATE_FORMAT } from "@/utils/datetime"
import { getActiveOrdersDefaultDates } from "@/hooks/useOrdersFilters"
import dynamic from "next/dynamic"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"

const DynamicReactJson = dynamic(import("react-json-view"), { ssr: false })

const WebhookLogStatusRender = (status: WebhookLogStatus) => {
  let textColor = ""
  let bgColor = ""
  switch (status) {
    case WebhookLogStatus.SUCCEEDED:
      textColor = "text-green-600"
      bgColor = "bg-green-200"
      break
    case WebhookLogStatus.FAILED:
      textColor = "text-red-600"
      bgColor = "bg-red-100"
      break
    default:
      textColor = "text-black-600"
      bgColor = "bg-black-300"
  }

  return (
    <div
      className={`px-3 py-0.5 ${bgColor} rounded-full flex items-center justify-center max-w-[150px]`}
    >
      <p className={textColor}>{capitalize(status)}</p>
    </div>
  )
}

const columns = () => {
  const tableColumns = [
    {
      title: "STATUS",
      key: "1",
      width: "18%",
      render: (log: IWebhookLog) => {
        return (
          <p className="flex-shrink-0 my-auto whitespace-nowrap">
            {WebhookLogStatusRender(log.status)}
          </p>
        )
      },
    },
    // {
    //   title: "EVENT TYPE",
    //   key: "2",
    //   render: (log: IWebhookLog) => {
    //     return <p className="flex-shrink-0 my-auto whitespace-wrap">{log.eventType}</p>
    //   },
    // },
    {
      title: "EVENT ID",
      key: "3",
      width: "50%",
      render: (log: IWebhookLog) => {
        return <p className="flex-shrink-0 my-auto whitespace-wrap">{log.eventId}</p>
      },
    },
    {
      title: "EVENT CREATED",
      key: "4",
      width: "22%",
      render: (log: IWebhookLog) => {
        return (
          <p className="flex-shrink-0 my-auto whitespace-wrap">
            {DateTime.fromMillis(log.createdAt).toFormat("dd-MM-yyyy HH:mm:ss")}
          </p>
        )
      },
    },
    Table.EXPAND_COLUMN,
  ]

  return tableColumns
}

interface WebhooksLogTableProps {
  viewDetailWebhook: IWebhook | null
}

const PAGE_SIZE = 10

const WebhooksLogTable: React.FC<WebhooksLogTableProps> = ({ viewDetailWebhook }) => {
  const { t } = useTranslation()
  const { preferredDateFormat } = useRecoilValue(userAtom)
  const [isLoading, setIsLoading] = React.useState(false)
  const [logs, setLogs] = React.useState<IWebhookLog[]>([])
  const [metadata, setMetadata] = React.useState({
    total: 0,
  })
  const [filters, setFilters] = React.useState<GetWebhookLogsFilters>({
    limit: PAGE_SIZE,
    offset: 0,
    dates: {
      range: [
        DateTime.now().startOf("year").toFormat(LUXON_PICKER_DATE_FORMAT),
        DateTime.now().endOf("day").toFormat(LUXON_PICKER_DATE_FORMAT),
      ],
      format: LUXON_PICKER_DATE_FORMAT,
    },
  })

  const fetchWebhookLogs = async () => {
    if (!viewDetailWebhook?.id) return
    setIsLoading(true)
    try {
      const { data } = await getWebhookLogs(viewDetailWebhook.id, filters)
      setLogs(data.logs)
      setMetadata(data.metadata)
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchWebhookLogs()
  }, [])

  useEffect(() => {
    fetchWebhookLogs()
  }, [filters])

  const onPageChange = (page: number) =>
    setFilters((filtersState) => {
      return {
        ...filtersState,
        offset: (page - 1) * PAGE_SIZE,
      }
    })

  const webhookFiltersComponents = () => {
    return (
      <>
        <div
          className="flex flex-row justify-between mb-4"
          style={{
            marginTop: "-1.5em",
          }}
        >
          <div className="flex gap-x-2 gap-y-2 flex-wrap">
            <SearchFilter
              size="middle"
              // filters={filters}
              searchValue={filters?.event_id}
              handleSearchValue={(searchValue) => {
                setFilters((state) => ({ ...state, event_id: searchValue }))
              }}
              showTooltip={false}
              placeholder={t("common.search.byEventId")}
            />
            <SearchFilter
              size="middle"
              // filters={filters}
              searchValue={filters?.order_id}
              handleSearchValue={(searchValue) => {
                setFilters((state) => ({ ...state, order_id: searchValue }))
              }}
              showTooltip={false}
              placeholder={t("common.search.byOrderId")}
            />
            <DateRangeFilter
              dates={filters.dates || getActiveOrdersDefaultDates(preferredDateFormat)}
              setDates={(dates: DateFilterType) =>
                setFilters((filtersState) => ({ ...filtersState, dates }))
              }
              selectTimeDisabled={false}
            />
          </div>
        </div>
      </>
    )
  }

  const tableComponent = () =>
    isLoading ? (
      <Loader fontSize={48} />
    ) : (
      <Table
        id="webhooks-log-table"
        className="order-table !mt-0"
        size="small"
        rowKey={"id"}
        dataSource={logs}
        columns={columns()}
        pagination={{
          position: ["bottomLeft"],
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          onChange: onPageChange,
          total: metadata?.total || 0,
          current: Math.ceil((filters.offset ?? 0) / (filters.limit ?? PAGE_SIZE)) + 1,
        }}
        expandable={{
          expandRowByClick: true,
          rowExpandable: (record) => record.description !== "" || record.data !== "",
          expandedRowRender: (record) => {
            const jsonData = JSON.parse(record.data)
            return (
              <div>
                <span className="font-semibold mr-1">{t("common.description")}:</span>
                <span>{record.description || "ok"}</span>
                <div className="mt-2">
                  <span className="font-semibold mr-1">{"Request"}:</span>
                  <DynamicReactJson
                    src={jsonData}
                    theme={{
                      base00: "white",
                      base01: "#ddd",
                      base02: "#ddd",
                      base03: "#444",
                      base04: "purple",
                      base05: "#444",
                      base06: "#444",
                      base07: "#444",
                      base08: "#444",
                      base09: "#4100ff",
                      base0A: "#4100ff",
                      base0B: "#4100ff",
                      base0C: "#4100ff",
                      base0D: "#4100ff",
                      base0E: "#4100ff",
                      base0F: "#4100ff",
                    }}
                  />
                </div>
              </div>
            )
          },
          expandIcon: (props: any) => OrderTableExpandIcon(props),
        }}
      />
    )

  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        <div>{webhookFiltersComponents()}</div>
      </div>
      {tableComponent()}
    </div>
  )
}

export default WebhooksLogTable
